import { useState, useRef, useEffect, useId, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useAnchoredMenu } from './useAnchoredMenu'
import { useEscapeLayer } from './escapeLayer'
import { listboxMove, openingIndex, typeAheadIndex } from './listboxNav'

export interface DropdownOption {
  value: string
  label: string
  badge?: { text: string; className?: string }
  /** Shown but not selectable. Prefer this over filtering the option out when
   * its absence would be a mystery: the user can see the row exists and the
   * badge explains what is missing. */
  disabled?: boolean
}

function OptionBadge({ badge }: { badge: NonNullable<DropdownOption['badge']> }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-none',
        badge.className ?? 'bg-gray-100 text-gray-600',
      )}
    >
      {badge.text}
    </span>
  )
}

interface DropdownSelectProps {
  value: string
  onChange: (value: string) => void
  options: DropdownOption[]
  placeholder?: string
  ariaLabel?: string
  hasError?: boolean
  disabled?: boolean
  className?: string
  leftIcon?: ReactNode
  size?: 'sm' | 'md' | 'input'
  /** Focus the trigger on mount, so a dialog opening on a select is keyboard-ready. */
  autoFocus?: boolean
  /** Id of the trigger button, so a FieldLabel or `<label htmlFor>` can name it. */
  id?: string
  required?: boolean
}

export function DropdownSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  ariaLabel,
  hasError = false,
  disabled = false,
  className,
  leftIcon,
  size = 'md',
  autoFocus = false,
  id,
  required,
}: DropdownSelectProps) {
  const [open, setOpen] = useState(false)
  // Index of the keyboard-highlighted option (aria-activedescendant) while open.
  const [active, setActive] = useState(-1)
  const baseId = useId()
  const listId = `${baseId}-listbox`
  const optionId = (i: number) => `${baseId}-opt-${i}`
  const btnRef = useRef<HTMLButtonElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.value === value)

  const dropPos = useAnchoredMenu({
    open,
    anchorRef: btnRef,
    menuRef: dropRef,
    listRef,
    revision: options.length,
    maxListHeight: 240,
  })

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      const t = e.target as Node
      if (
        btnRef.current &&
        !btnRef.current.contains(t) &&
        dropRef.current &&
        !dropRef.current.contains(t)
      )
        setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Escape is the only way out for a keyboard user: the panel is portaled to
  // document.body, so tabbing away does not dismiss it. It closes this list
  // only (never the dialog around it) and hands focus back to the trigger.
  useEscapeLayer(open, () => {
    setOpen(false)
    btnRef.current?.focus()
  })

  // Keep the highlighted option in view as the arrow keys move it.
  useEffect(() => {
    if (!open || active < 0) return
    document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- optionId is derived from the stable baseId
  }, [open, active])

  function openList() {
    setActive(
      openingIndex(
        options,
        options.findIndex((o) => o.value === value),
      ),
    )
    setOpen(true)
  }

  function choose(index: number) {
    const o = options[index]
    if (!o || o.disabled) return
    onChange(o.value)
    setOpen(false)
    btnRef.current?.focus()
  }

  // Select-only combobox keyboard model (WAI-ARIA APG): arrows open the list
  // and move the highlight, Enter or Space picks, Home/End jump, a letter
  // jumps to the next option starting with it, Tab closes and moves on.
  // Q6-FD6: typed characters build a short buffer ("Mini") so type-ahead
  // matches the whole prefix, not each letter on its own. A key typed while
  // the list is closed opens it on the match.
  const typeBuf = useRef({ text: '', at: 0 })
  function typeAhead(char: string, from: number): number {
    const now = Date.now()
    const buf = typeBuf.current
    buf.text = now - buf.at > 700 ? char : buf.text + char
    buf.at = now
    const repeated = buf.text.length > 1 && buf.text.split('').every((c) => c === buf.text[0])
    return typeAheadIndex(options, from, repeated ? char : buf.text)
  }

  function onTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return
    const printable = e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault()
        openList()
      } else if (printable) {
        e.preventDefault()
        const start = options.findIndex((o) => o.value === value)
        const hit = typeAhead(e.key, start)
        setActive(hit >= 0 ? hit : openingIndex(options, start))
        setOpen(true)
      }
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      choose(active)
      return
    }
    if (e.key === 'Tab') {
      setOpen(false)
      return
    }
    const moved = listboxMove(options, active, e.key)
    if (moved !== null) {
      e.preventDefault()
      if (moved >= 0) setActive(moved)
      return
    }
    if (printable) {
      const hit = typeAhead(e.key, active)
      if (hit >= 0) setActive(hit)
    }
  }

  // Q4-BPOS3: the list is portalled INTO an enclosing dialog so it stays in
  // the modal's accessibility tree and focus scope. A dialog that animates
  // with a transform becomes the containing block for `position: fixed`, so
  // viewport coordinates would land offset by the dialog's own position;
  // subtract that box when (and only when) the host establishes one.
  const portalHost = open
    ? (btnRef.current?.closest('[role="dialog"]') as HTMLElement | null)
    : null
  const portalOffset = fixedContainingOffset(portalHost)

  function handleOpen() {
    if (disabled) return
    if (open) setOpen(false)
    else openList()
  }

  return (
    <div className={cn('relative', className)}>
      <button
        ref={btnRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        // Q4-BPOS3: the listbox is portalled to <body> (so it escapes the
        // dialog's overflow clip), which put it outside an aria-modal dialog
        // and hid every option from screen readers. aria-owns re-parents it
        // under the combobox in the accessibility tree.
        aria-owns={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        // A combobox is not named by its content, so the selected text no
        // longer names it. Fall back to the placeholder; a FieldLabel above
        // sets aria-labelledby, which outranks this. Once something is
        // picked, the pick names it (Q6-HK3: it stayed "Unassigned").
        aria-label={ariaLabel ?? selected?.label ?? placeholder}
        aria-invalid={hasError || undefined}
        aria-required={required || undefined}
        disabled={disabled}
        autoFocus={autoFocus}
        onClick={handleOpen}
        onKeyDown={onTriggerKeyDown}
        className={cn(
          size === 'sm'
            ? 'w-full h-8 px-3 rounded-lg bg-white border text-xs flex items-center gap-2 transition-shadow duration-150'
            : size === 'input'
              ? 'w-full h-[34px] px-2.5 rounded-lg bg-white border text-sm font-medium flex items-center gap-2 transition-shadow duration-150'
              : 'w-full h-9 px-3.5 rounded-lg bg-white border text-sm flex items-center gap-2 transition-shadow duration-150',
          hasError
            ? 'border-red-400'
            : open
              ? 'border-brand-orange shadow-input-focus'
              : 'border-gray-200 hover:border-gray-300',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        {leftIcon && <span className="shrink-0 text-gray-400">{leftIcon}</span>}
        <span
          className={cn(
            'flex-1 text-left truncate font-normal',
            selected ? 'text-brand-rail' : 'text-gray-400',
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        {selected?.badge && <OptionBadge badge={selected.badge} />}
        <ChevronDown
          size={14}
          className={cn('shrink-0 text-gray-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropRef}
            className="fixed z-[9999] rounded-xl border border-gray-100 bg-white shadow-lg overflow-hidden"
            style={{
              top: dropPos.top - portalOffset.top,
              left: dropPos.left - portalOffset.left,
              minWidth: dropPos.width,
            }}
          >
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              aria-label={ariaLabel ?? placeholder}
              className="no-scrollbar overflow-y-auto py-1"
              style={{ maxHeight: dropPos.listMaxHeight }}
            >
              {options.map((o, i) => (
                <button
                  key={o.value}
                  id={optionId(i)}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={o.value === value}
                  aria-disabled={o.disabled || undefined}
                  disabled={o.disabled}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => !o.disabled && setActive(i)}
                  onClick={() => choose(i)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                    o.disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : o.value === value
                        ? 'bg-brand-orange/5 text-brand-orange'
                        : 'text-brand-rail hover:bg-gray-50',
                    i === active && !o.disabled && 'bg-gray-100',
                  )}
                >
                  <span className="flex-1 whitespace-nowrap">{o.label}</span>
                  {o.badge && <OptionBadge badge={o.badge} />}
                  {o.value === value && <Check size={12} className="text-brand-orange shrink-0" />}
                </button>
              ))}
            </div>
          </div>,
          // Q4-BSTOCK4: inside an aria-modal dialog, a list portalled to <body>
          // sits outside the modal and drops out of the accessibility tree, so
          // screen readers saw a combobox with no options. Portal into the
          // dialog (the list is `fixed`, so it is not clipped by its scroll).
          portalHost ?? document.body,
        )}
    </div>
  )
}

/** Q4-BPOS3: where a `position: fixed` child of `host` is measured from.
 * Zero unless `host` has a transform, filter or perspective (which make it
 * the containing block for fixed descendants). */
export function fixedContainingOffset(host: HTMLElement | null): { top: number; left: number } {
  if (!host || typeof window === 'undefined') return { top: 0, left: 0 }
  const cs = window.getComputedStyle(host)
  const contains =
    (cs.transform && cs.transform !== 'none') ||
    (cs.filter && cs.filter !== 'none') ||
    (cs.perspective && cs.perspective !== 'none') ||
    /paint|layout|strict|content/.test(cs.contain || '')
  if (!contains) return { top: 0, left: 0 }
  // The containing block is the PADDING box, and a fixed child of a
  // containing ancestor scrolls with that ancestor's content: step in by the
  // border and back out by the scroll offset.
  const r = host.getBoundingClientRect()
  return {
    top: r.top + (host.clientTop || 0) - (host.scrollTop || 0),
    left: r.left + (host.clientLeft || 0) - (host.scrollLeft || 0),
  }
}
